import type { EditorPlugin, FabricEditor } from 'modules/fabric-editor';
import { FabricPluginName } from 'modules/fabric-editor';
import {
  createLogoGroup,
  DEFAULT_LOGO_MARGIN,
  DEFAULT_LOGO_SCALE,
  DEFAULT_LOGO_SLOT,
  findLogo,
  getLogoData,
  MAX_LOGO_MARGIN,
  MIN_LOGO_MARGIN,
  scaleBoundsForVariant,
  variantForSlot,
} from './logo-factory';
import type { LogoFacade, LogoSlot } from './types';

declare module 'modules/fabric-editor/domain/core/fabric-editor' {
  interface FabricEditor {
    logo?: LogoFacade;
  }
}

/**
 * Business plugin (impl in modules/manager) owning the mandatory ImGame logo.
 * Ensures the logo exists after every template load and keeps it pinned above
 * all other content. The core layer plugin honors `data.pin === 'top'` so the
 * layer buttons/hotkeys can never move a normal object above it.
 */
export function logoPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.LOGO,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;

      const ensure = () => {
        const existing = findLogo(canvas);
        if (existing) {
          canvas.bringToFront(existing);
          return;
        }
        const group = createLogoGroup(DEFAULT_LOGO_SLOT, DEFAULT_LOGO_SCALE, DEFAULT_LOGO_MARGIN, editor.resolution);
        canvas.add(group);
        canvas.bringToFront(group);
        canvas.requestRenderAll();
      };

      // Rebuilds the logo group for a new slot/scale/margin. Runs inside a
      // history transaction: the remove+add is a programmatic re-derivation
      // (variant may change between slots), not two discrete user edits, and
      // must record at most one undo step (criteria). createLogoGroup clamps the
      // scale to the target variant and the margin to its range.
      const rebuild = (slot: LogoSlot, scale: number, margin: number) => {
        const run = () => {
          const existing = findLogo(canvas);
          if (existing) canvas.remove(existing);
          const group = createLogoGroup(slot, scale, margin, editor.resolution);
          canvas.add(group);
          canvas.bringToFront(group);
        };
        if (editor.history) editor.history.transaction(run);
        else run();
        canvas.requestRenderAll();
        const logo = findLogo(canvas);
        if (logo) editor.emit('object:modified', { target: logo });
      };

      const offLoaded = editor.on('canvas:loaded', () => ensure());
      const offAdded = editor.on('object:added', (e) => {
        const logo = findLogo(canvas);
        // Any newly-added content sinks below the logo; skip our own inserts.
        if (logo && e.target !== logo) canvas.bringToFront(logo);
      });

      const facade: LogoFacade & { __off?: () => void } = {
        ensure,
        get: () => findLogo(canvas),
        getPosition: () => getLogoData(findLogo(canvas))?.position,
        setPosition: (slot) => {
          const data = getLogoData(findLogo(canvas));
          rebuild(slot, data?.scale ?? DEFAULT_LOGO_SCALE, data?.margin ?? DEFAULT_LOGO_MARGIN);
        },
        getScale: () => getLogoData(findLogo(canvas))?.scale,
        setScale: (scale) => {
          const data = getLogoData(findLogo(canvas));
          rebuild(data?.position ?? DEFAULT_LOGO_SLOT, scale, data?.margin ?? DEFAULT_LOGO_MARGIN);
        },
        getScaleBounds: () => {
          const slot = getLogoData(findLogo(canvas))?.position ?? DEFAULT_LOGO_SLOT;
          return scaleBoundsForVariant(variantForSlot(slot));
        },
        getMargin: () => getLogoData(findLogo(canvas))?.margin,
        setMargin: (margin) => {
          const data = getLogoData(findLogo(canvas));
          rebuild(data?.position ?? DEFAULT_LOGO_SLOT, data?.scale ?? DEFAULT_LOGO_SCALE, margin);
        },
        getMarginBounds: () => ({ min: MIN_LOGO_MARGIN, max: MAX_LOGO_MARGIN }),
      };
      facade.__off = () => {
        offLoaded();
        offAdded();
      };
      editor.logo = facade;
    },
    dispose(editor: FabricEditor) {
      const facade = editor.logo as (LogoFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.logo = undefined;
    },
  };
}
